import React from 'react';
import { Typography } from '@mui/material';
import LegalPageLayout from './LegalPageLayout';
import { useTenantConfig } from '../../contexts/TenantConfigContext';

const PrivacyPolicyPage: React.FC = () => {
  const { config } = useTenantConfig();
  const brandName = config?.branding?.displayName || 'RAF';
  const contactEmail = config?.accountManager?.email || 'support@raf.local';

  return (
    <LegalPageLayout title="Privacy Policy" lastUpdated="17 March 2026">
      <Typography component="h2">1. Introduction</Typography>
      <Typography component="p">
        This Privacy Policy explains how {brandName} (&quot;we&quot;, &quot;us&quot;, or
        &quot;our&quot;) collects, uses, stores, and protects your personal information when you use
        our platform and services (the &quot;Service&quot;).
      </Typography>
      <Typography component="p">
        We are committed to protecting your privacy and handling your data in a transparent manner.
        By using the Service, you consent to the practices described in this policy.
      </Typography>

      <Typography component="h2">2. Information We Collect</Typography>

      <Typography component="h3">2.1 Account Information</Typography>
      <Typography component="p">
        When you create an account, we collect your name, email address, and authentication
        credentials. If you subscribe to a paid plan, payment processing is handled by Paddle.com
        Market Ltd (&quot;Paddle&quot;), our Merchant of Record — we do not store your credit card
        or banking details directly.
      </Typography>

      <Typography component="h3">2.2 Content You Upload</Typography>
      <Typography component="p">
        When you use the Service, you may upload audio files, documents, or paste text for
        processing. This content is processed to deliver the Service and stored temporarily in
        encrypted form on our cloud infrastructure (Amazon Web Services).
      </Typography>

      <Typography component="h3">2.3 Usage Data</Typography>
      <Typography component="p">
        We automatically collect usage information such as pages visited, features used, timestamps,
        browser type, operating system, and IP address. This helps us improve the Service and
        diagnose technical issues.
      </Typography>

      <Typography component="h3">2.4 Cookies</Typography>
      <Typography component="p">
        We use essential cookies to maintain your authenticated session. We do not use third-party
        tracking or advertising cookies.
      </Typography>

      <Typography component="h2">3. How We Use Your Information</Typography>
      <Typography component="p">We use your information to:</Typography>
      <ul>
        <li>Provide, maintain, and improve the Service;</li>
        <li>Process your uploaded content (transcription, extraction, transformation);</li>
        <li>Manage your account, subscriptions, and billing (via Paddle);</li>
        <li>Send essential service communications (account confirmations, billing notices);</li>
        <li>Monitor and prevent fraud, abuse, and security threats;</li>
        <li>Comply with legal obligations.</li>
      </ul>
      <Typography component="p">
        We do <strong>not</strong> use your uploaded content to train AI models. Your content is
        processed solely to return results to you and is not shared with or sold to third parties.
      </Typography>

      <Typography component="h2">4. Data Sharing</Typography>
      <Typography component="p">
        We share your personal information only in the following limited circumstances:
      </Typography>
      <ul>
        <li>
          <strong>Paddle (Merchant of Record)</strong>: We share your email address and subscription
          details with Paddle to process payments and manage subscriptions.
        </li>
        <li>
          <strong>Cloud Infrastructure</strong>: Your data is processed and stored on Amazon Web
          Services (AWS) infrastructure, subject to AWS&apos;s security and compliance standards.
        </li>
        <li>
          <strong>AI Processing</strong>: Audio and text content is sent to third-party AI
          providers for processing. These providers process data per their enterprise terms and
          do not retain your data for model training.
        </li>
        <li>
          <strong>Legal Requirements</strong>: We may disclose information if required to do so by
          law or in response to valid legal process.
        </li>
      </ul>
      <Typography component="p">
        We do not sell your personal information to any third party.
      </Typography>

      <Typography component="h2">5. Data Retention</Typography>
      <Typography component="p">
        Uploaded files and processing outputs are retained for a limited period to enable you to
        access your results, after which they are automatically deleted. Account information is
        retained for as long as your account is active. If you request account deletion, we will
        remove your personal data within 30 days, except where retention is required by law.
      </Typography>

      <Typography component="h2">6. Data Security</Typography>
      <Typography component="p">
        We implement industry-standard security measures to protect your data, including:
      </Typography>
      <ul>
        <li>Encryption in transit (TLS/HTTPS) and at rest (AES-256);</li>
        <li>Access controls with role-based permissions;</li>
        <li>Regular security monitoring and logging;</li>
        <li>Secure authentication via AWS Cognito.</li>
      </ul>
      <Typography component="p">
        While we take reasonable steps to protect your data, no system is completely secure. You are
        responsible for keeping your account credentials confidential.
      </Typography>

      <Typography component="h2">7. Your Rights</Typography>
      <Typography component="p">
        Depending on your jurisdiction, you may have the right to:
      </Typography>
      <ul>
        <li>Access the personal data we hold about you;</li>
        <li>Request correction of inaccurate data;</li>
        <li>Request deletion of your personal data;</li>
        <li>Object to or restrict certain processing activities;</li>
        <li>Request data portability;</li>
        <li>Withdraw consent where processing is based on consent.</li>
      </ul>
      <Typography component="p">
        To exercise these rights, contact us at{' '}
        <a href={`mailto:${contactEmail}`}>{contactEmail}</a>. We will respond within 30 days.
      </Typography>

      <Typography component="h2">8. International Data Transfers</Typography>
      <Typography component="p">
        Our Service is hosted on AWS infrastructure. Your data may be processed in regions outside
        your country of residence. We ensure that appropriate safeguards are in place in accordance
        with applicable data protection laws.
      </Typography>

      <Typography component="h2">9. Children&apos;s Privacy</Typography>
      <Typography component="p">
        The Service is not directed at children under 18. We do not knowingly collect personal
        information from children. If we become aware that we have collected data from a child, we
        will take steps to delete it promptly.
      </Typography>

      <Typography component="h2">10. Changes to This Policy</Typography>
      <Typography component="p">
        We may update this Privacy Policy from time to time. If we make material changes, we will
        notify you via email or through the Service at least 14 days before the changes take
        effect. The &quot;Last updated&quot; date at the top indicates when this policy was last
        revised.
      </Typography>

      <Typography component="h2">11. Contact</Typography>
      <Typography component="p">
        If you have questions about this Privacy Policy or how we handle your data, contact us at{' '}
        <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.
      </Typography>
    </LegalPageLayout>
  );
};

export default PrivacyPolicyPage;
